// apps/client/src/game/game.ts — the live town on the client (§8.3, §8.6, §8.7): one socket, the space scenes, you and the other
// residents, the fixed-step systems, and the one command API the UI calls. The constructor touches no DOM (tests import it);
// `attach()` creates the renderer, camera, input and name tags on the first town entry.
import { tuning, type StringKey } from '@whiskerton/content';
import {
  townTime, type DayKey, type EventKind, type EventOf, type MapData, type MoveState, type NavGrid, type ObjectId, type ObjectKind, type Occupant,
  type PrivatePatch, type ResidentId, type ResidentPublic, type S2C, type Snapshot, type SpaceId, type SpaceSnapshot, type TileXY, type TownEvent,
  type TownId, type Vec2, type WorldObject, type ApiErrorCode, CLOSE,
} from '@whiskerton/shared';
import { buildNavGrid, findPath } from '@whiskerton/shared/map/nav';   // subpath-only (not in the barrel)
import * as THREE from 'three';
import { api as defaultApi, type Api } from '../api/endpoints';
import { pageAssets } from '../assets/shared';
import type { AssetService } from '../assets/service';
import { moodAt } from '../audio/mood';
import { pageSound } from '../audio/music';
import type { Sound } from '../audio/sound';
import { IS_DEV } from '../boot/env';
import { Avatar } from '../characters/avatar';
import { lookToDoll } from '../characters/look';
import { NameTags, residentHouseTags } from '../characters/nametags';
import { RemoteResident } from '../characters/remote';
import { Walker } from '../characters/walker';
import { Loop } from '../engine/loop';
import { PerfOverlay } from '../engine/perf';
import { resolveQuality, type QualityLevel } from '../engine/quality';
import { pageCanvas, pageRenderer } from '../engine/pageRenderer';
import { addSilhouette } from '../engine/silhouette';
import type { Renderer } from '../engine/renderer';
import { FADE_VIEWERS, setFadeCamera, setViewers, worldUniforms } from '../engine/shader/worldPatch';
import type { Frame, System, SystemName } from '../engine/systems';
import { Input } from '../input/input';
import { TapToWalk } from '../input/tapToWalk';
import { netHandlerModules } from '../net/handlers.gen';
import { handlers as coreHandlers } from '../net/handlers/core';
import { TownSocket, type SocketState } from '../net/socket';
import type { ClientSettings } from '../state/slices/core';
import { useGame } from '../state/store';
import { decorateFraming, FollowCamera, roomFraming, type DecorateUi, type Framing } from '../world/camera';
import { WALL_HEIGHT } from '../world/interior';
import type { OutdoorScene } from '../world/outdoor';
import { pickGround, type PickHit } from '../world/picking';
import { Bus, type GameBusEvents } from './bus';
import { commandModules } from './commands.gen';
import { InteractionSystem } from './interact';
import { SpaceManager, type SpaceScene } from './spaces';
import { systemModules } from './systems.gen';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- augmented by every game/commands/*.ts file
export interface GameCommands {}
export type CommandFactory<C> = (game: Game) => C;
/** Contract for the net/handlers registry (§8.6). All modules' handlers for the same hook run in module order. */
export interface NetHandlers {
  events?: { [K in EventKind]?: (e: EventOf<K>, game: Game) => void };
  onSnapshot?(snap: Snapshot, game: Game): void;            // after the core applied a snapshot (render your layer)
  onSpace?(space: SpaceSnapshot, game: Game): void;          // after entering a space (incl. 'out' on return and the snapshot's space)
  onPatch?(patch: PrivatePatch, game: Game): void;           // after ack.priv or `you.patch` was applied to the store
}
/** game/systems registry — extra per-frame work slotted into the fixed order (fishing bobber M1-T15, villagers M2-T11). */
export interface ExtSystem { slot: SystemName; create(game: Game): System }
/** Contract change 34: test seams, passed to TownSocket's deps. getGame() still calls `new Game()`. */
export interface GameOptions { wsFactory?: (url: string) => WebSocket; api?: Api }

/** Writes WorldSlice.clock from the synced town clock and emits `hour:changed` / `day:changed` when the town hour or day key
 *  turns (never on the first tick after a reset, i.e. after a snapshot). */
export class ClockTicker {
  private last: { hourKey: string; dayKey: string } | null = null;
  private readonly bus: Bus<GameBusEvents>;
  private readonly write: (c: { minuteOfDay: number; weekday: number; dayKey: DayKey }) => void;
  constructor(bus: Bus<GameBusEvents>, write: (c: { minuteOfDay: number; weekday: number; dayKey: DayKey }) => void) { this.bus = bus; this.write = write; }
  reset(): void { this.last = null; }                                // after every snapshot: the first tick never emits
  tick(epochMs: number, timeZone: string): void {
    const tt = townTime(epochMs, timeZone);
    this.write({ minuteOfDay: tt.minuteOfDay, weekday: tt.weekday, dayKey: tt.dayKey });
    if (this.last) {
      if (tt.hourKey !== this.last.hourKey) this.bus.emit('hour:changed', { hourKey: tt.hourKey });
      if (tt.dayKey !== this.last.dayKey) this.bus.emit('day:changed', { dayKey: tt.dayKey });
    }
    this.last = { hourKey: tt.hourKey, dayKey: tt.dayKey };
  }
}

type Modules = readonly { handlers: NetHandlers }[];
const handlerModules = (): Modules => netHandlerModules as Modules;
const TAP_RESIDENT = 0.6;                         // tiles: a tap this close to another resident hits them (precision, not feel)
const PERF_EVERY = 15;                            // frames between perf overlay refreshes
const MAX_REPLANS = 3;                            // corrections one tap-to-walk survives before it gives up (a guard against loops, not feel)
/** Who is in your space: held during a scene switch and replayed after it (presence.leave too, so it never precedes a held enter). */
const HELD_WHILE_SWITCHING: ReadonlySet<EventKind> = new Set<EventKind>(['space.enter', 'space.leave', 'presence.leave']);
const deviceInfo = () => ({
  coarse: typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches,
  cores: typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 4 : 4,
  memoryGb: typeof navigator !== 'undefined' ? ((navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null) : null,
});
const lookKey = (r: ResidentPublic) => `${r.color}|${JSON.stringify(r.look)}`;
type Me = { r: ResidentId; avatar: Avatar; walker: Walker; key: string };

/** What to tell you when the server stopped your socket: another device took over, a refused ticket, a closed town, or (M2 playtest:
 *  removed by the mayor while online, with no word of it) that you no longer live in that town. Removal reaches you as the town's
 *  not-a-resident close, or, when your socket was down at the time (a background tab dropped as silent), as the reconnect's ticket
 *  refused with 403 forbidden: the ticket route's answer to anyone who is not a resident of that town (v1 round 3). */
export function stopNotice(close: number | null, http: ApiErrorCode | null, townName: string | null): { key: StringKey; params?: Record<string, string> } {
  if (close === CLOSE.replaced) return { key: 'hud.replaced' };
  if (http === 'forbidden' && townName) return { key: 'hud.removedFromTown', params: { town: townName } };
  if (http) return { key: `errors.${http}` };
  if (close === CLOSE.townClosed) return { key: 'errors.town_archived' };
  if (close === CLOSE.notResident && townName) return { key: 'hud.removedFromTown', params: { town: townName } };
  return { key: 'errors.forbidden' };
}

export class Game {
  readonly cmd: GameCommands;
  readonly bus = new Bus<GameBusEvents>();
  /** The town socket (§8.7 `readonly socket`): only connect() and teardown() replace it. */
  get socket(): TownSocket | null { return this.sock; }
  readonly assets: AssetService;
  readonly loop: Loop;
  readonly sound: Sound;
  readonly scenes: { out: OutdoorScene | null; current: SpaceScene | null };
  readonly spaces: SpaceManager;
  readonly interactions: InteractionSystem;
  readonly ticker: ClockTicker;
  readonly tapToWalk = new TapToWalk(() => this.detour());
  /** The town map (fromPayload of the last snapshot), for features and finders. */
  map: MapData | null = null;
  tags: NameTags | null = null;
  renderer: Renderer | null = null;
  camera: FollowCamera | null = null;
  input: Input | null = null;
  private readonly opts: GameOptions;
  private sock: TownSocket | null = null;
  /** space.enter / space.leave held while a switch builds the new scene; null when not switching (see switchSpace). */
  private spaceEvents: TownEvent[] | null = null;
  private sceneSwitches = 0;
  private visit = 0;                                              // bumped by teardown: a late switch of the last town changes nothing
  private me: Me | null = null;
  private meBuild = 0;
  /** Where you were last put down in the current space, for a doll that finishes building after the switch already ran. */
  private lastSpawn: MoveState | null = null;
  private readonly remotes = new Map<ResidentId, RemoteResident>();
  private readonly spawning = new Map<ResidentId, number>();
  private spawnSeq = 0;
  private readonly held = new Map<ResidentId, string | null>();
  private readonly looks = new Map<ResidentId, string>();
  private readonly outObjects = new Map<ObjectId, WorldObject>();
  private navGrid: NavGrid | null = null;
  private navDirty = true;
  private attached = false;
  private switching = 0;
  private snapRun = 0;
  private intent = { dx: 0, dz: 0, running: false };
  private lastClockTick = -Infinity;
  /** The tile the current tap-to-walk heads for, and how often a correction has re-planned it. */
  private walkGoal: TileXY | null = null;
  private walkReplans = 0;
  private perf: PerfOverlay | null = null;
  private rendered = false;
  /** While ready() waits on the first town frame: frames still to draw once the scene's shaders compiled (-1 = still compiling). */
  private settle: { frames: number } | null = null;
  private frameWaiters: (() => void)[] = [];
  private liveWaiters: (() => void)[] = [];
  private readonly heads = new Map<ResidentId, THREE.Vector3>();
  /** Your walker's view of the current space, whichever scene that is. */
  private readonly here: Pick<SpaceScene, 'heightAt' | 'walkable' | 'canMove'> = {
    heightAt: (p) => this.scenes.current?.heightAt(p) ?? 0,
    walkable: (p) => this.scenes.current?.walkable(p) ?? false,
    canMove: (a, b) => { const c = this.scenes.current; return c ? (c.canMove ? c.canMove(a, b) : c.walkable(b)) : false; },
  };

  constructor(opts: GameOptions = {}) {
    this.opts = opts;
    this.assets = pageAssets();                                  // the page's: the title screen's showcase town loaded the kits into it
    this.sound = pageSound();                                    // the page's one engine: the title theme plays on it before any town
    this.ticker = new ClockTicker(this.bus, (clock) => useGame.setState({ clock }));
    const spaces = new SpaceManager(this);
    this.spaces = spaces;
    this.scenes = { out: null, get current() { return spaces.current; }, set current(s) { spaces.current = s; } };
    this.interactions = new InteractionSystem(this);
    this.cmd = Object.assign({}, ...(commandModules as readonly { commands: CommandFactory<object> }[]).map((m) => m.commands(this))) as GameCommands;
    this.bus.on('space:changed', () => this.syncHouseTags());
    this.loop = new Loop(this.systems(), () => this.now());
  }

  /** The synced town clock (epoch ms); the device clock only until the first snapshot. */
  now(): number { return this.socket?.clock.now() ?? Date.now(); }
  you(): { pos: Vec2; f: number; space: SpaceId; avatar: Avatar } | null {
    const me = this.me;
    return me ? { pos: { x: me.walker.pos.x, y: me.walker.pos.z }, f: me.walker.yaw, space: useGame.getState().space, avatar: me.avatar } : null;
  }
  avatarOf(r: ResidentId): Avatar | null {
    if (this.me?.r === r) return this.me.avatar;
    return this.remotes.get(r)?.avatar ?? null;
  }
  /** Where the sun and sky follow you. */
  focus(): THREE.Vector3 | undefined { return this.me?.walker.pos; }
  /** The current outdoor objects (snapshot + object.* events), optionally of one kind. */
  objects(kind?: ObjectKind): WorldObject[] {
    const all = [...this.outObjects.values()];
    return kind ? all.filter((o) => o.kind === kind) : all;
  }
  /** Replaces the outdoor object index (an `out` snapshot); the nav grid follows. */
  setObjects(list: readonly WorldObject[]): void {
    this.outObjects.clear();
    for (const o of list) this.outObjects.set(o.id, o);
    this.navDirty = true;
  }
  /** The outdoor walkability grid, rebuilt lazily after `invalidateNav()`. */
  nav(): NavGrid | null {
    if (!this.map) return null;
    if (this.navDirty || !this.navGrid) { this.navGrid = buildNavGrid(this.map, [...this.outObjects.values()]); this.navDirty = false; }
    return this.navGrid;
  }
  invalidateNav(): void { this.navDirty = true; }
  remotePositions(): { id: ResidentId; x: number; y: number }[] {
    return [...this.remotes.values()].map((rr) => ({ id: rr.r, x: rr.pos.x, y: rr.pos.z }));
  }

  // ---------- lifecycle (driven by game/commands/core.ts)
  /** Takes over the page's renderer, and creates the camera, input and name tags once (the first town entry). */
  attach(): void {
    if (this.attached) return;
    this.attached = true;
    const canvas = pageCanvas(), level = resolveQuality(useGame.getState().settings.quality, deviceInfo());
    // The title's showcase town drew with this renderer (title screen, 2026-10-05), at the title's own quality: the town sets its own.
    this.renderer = pageRenderer(level);
    this.renderer.setQuality(level);
    this.camera = new FollowCamera(innerWidth / innerHeight);
    this.camera.attachGestures(canvas);
    const input = new Input(canvas);
    this.input = input;
    input.on('A', () => this.cmd.press('A'));
    input.on('B', () => this.cmd.press('B'));
    input.on('tool', () => this.cmd.press('tool'));
    input.onTap((ndc) => this.onTap(ndc));
    this.tags = new NameTags(this.uiRoot());
    window.addEventListener('resize', () => { this.renderer?.resize(); this.camera?.setAspect(innerWidth / innerHeight); });
  }
  /** Opens the town socket with this game's handlers and starts the loop. */
  connect(townId: TownId): void {
    const prev = this.sock;
    this.sock = null;                                             // first, so the old socket's 'closed' state is ignored
    prev?.close();                                                // never leave an orphan that would later replace this session
    this.tags ??= this.attached ? new NameTags(this.uiRoot()) : null;
    const socket: TownSocket = new TownSocket(townId, {
      api: this.opts.api ?? defaultApi,
      onSnapshot: (s) => { if (this.socket === socket) void this.onSnapshot(s, socket); },
      onMoves: (m) => { if (this.socket === socket) this.onMoves(m); },
      onCorrect: (c) => { if (this.socket === socket) this.onCorrect(c); },
      onEvents: (e) => { if (this.socket === socket) this.onEvents(e); },
      onState: (st) => { if (this.socket === socket) this.onState(st, socket); },
      wsFactory: this.opts.wsFactory,
    });
    this.sock = socket;
    socket.connect();
    this.loop.start();                                            // audio: Play now unlocked it; any later tap restarts it (installMusic)
  }
  /** Closes the socket, stops the loop and drops everything of this town (leaveTown). */
  teardown(): void {
    const socket = this.sock;
    this.sock = null;
    socket?.close();
    this.loop.stop();
    this.snapRun++; this.meBuild++;
    for (const r of [...this.remotes.keys()]) this.despawn(r);
    this.spawning.clear(); this.held.clear();
    this.me?.avatar.dispose();
    this.me = null;
    this.lastSpawn = null;
    this.tags?.dispose();
    this.tags = null;
    this.spaces.reset();                                          // keeps scenes.out: the next town rebuilds into it (no leak)
    this.spaceEvents = null; this.sceneSwitches = 0; this.visit++;
    this.outObjects.clear();
    this.map = null; this.navGrid = null; this.navDirty = true;
    this.tapToWalk.cancel();
    this.interactions.clear();
    this.perf?.dispose(); this.perf = null;
    // The sound engine is the page's (audio/music.ts): leaving the town, it crossfades back to the title theme on the towns list, at
    // the outdoor level even if you left from indoors.
    this.sound.setIndoor(false);
    this.rendered = false;
    this.settle = null;
    this.lastClockTick = -Infinity;
    this.ticker.reset();
  }
  /** Resolves once a town is on screen: its shaders compiled and two frames drawn with them (test hooks). */
  whenRendered(): Promise<void> {
    return this.rendered ? Promise.resolve() : new Promise((r) => this.frameWaiters.push(r));
  }
  /** Resolves once the town socket is live (its snapshot arrived), waiting through any reconnect. */
  whenLive(): Promise<void> {
    return this.sock?.state === 'live' ? Promise.resolve() : new Promise((r) => this.liveWaiters.push(r));
  }
  /** The test hooks' ready() (§8.11): a town on screen (whenRendered) whose socket is live, both at once. A first frame can take tens
   *  of seconds on a slow GPU (SwiftShader in e2e), long enough for the town to drop the silent socket and the client to reconnect. */
  async whenReady(): Promise<void> {
    for (;;) {
      await this.whenRendered();
      await this.whenLive();
      if (this.rendered && this.sock?.state === 'live') return;
    }
  }
  /** Space switch with movement held (no `mv` from the old space while the door fades). */
  async switchSpace(snap: SpaceSnapshot, spawn: MoveState): Promise<void> {
    this.switching++;
    const end = this.holdSpaceEvents(false);
    try { await this.spaces.switchTo(snap, spawn); } finally { this.switching--; end(); }
  }
  /** From the moment the server has moved you (the ack, or a snapshot) until the new scene has spawned `occupants`, the space's
   *  arrivals and departures are held, then replayed in order: applied at once they would land in the old scene and then be undone
   *  by spawnOccupants (whose list predates them). `fresh` (a snapshot) drops what was held: the snapshot already contains it. */
  private holdSpaceEvents(fresh: boolean): () => void {
    const visit = this.visit;
    if (fresh || !this.spaceEvents) this.spaceEvents = [];
    this.sceneSwitches++;
    return () => {
      if (visit !== this.visit || --this.sceneSwitches > 0) return;
      const held = this.spaceEvents ?? [];
      this.spaceEvents = null;
      if (held.length) this.onEvents(held);
    };
  }
  /** Holds movement while `p` runs (enterSpace: from the request until the new scene is up). */
  async holdMovement<T>(p: () => Promise<T>): Promise<T> {
    this.switching++;
    try { return await p(); } finally { this.switching--; }
  }
  /** Tap-to-walk to `tile` (next to it when it is blocked); `then` runs on arrival. False when there is no path. */
  /** `cancelled` runs instead when the walk is cancelled (B, the stick, a correction, a space change, a newer walk). */
  walkTo(tile: TileXY, then?: () => void, cancelled?: () => void): boolean {
    const path = this.planWalk(tile);
    if (!path) return false;
    this.tapToWalk.start(path, then, cancelled);
    this.walkGoal = tile; this.walkReplans = 0;
    return true;
  }
  /** The stuck walk's way round (TapToWalk): a path to the walk's goal that treats the tiles other residents stand on as blocked
   *  (outdoors only; an interior's straight walk has no way round). */
  private detour(): TileXY[] | null {
    const me = this.me, goal = this.walkGoal, map = this.map;
    const nav = useGame.getState().space === 'out' ? this.nav() : null;
    if (!me || !goal || !nav || !map) return null;
    const from = { x: Math.floor(me.walker.pos.x), y: Math.floor(me.walker.pos.z) }, walk = nav.walk.slice();
    for (const rr of this.remotes.values()) {
      const x = Math.floor(rr.pos.x), y = Math.floor(rr.pos.z);
      if (x >= 0 && y >= 0 && x < nav.w && y < nav.h && (x !== from.x || y !== from.y)) walk[y * nav.w + x] = 0;
    }
    return findPath({ ...nav, walk }, map, from, goal, { adjacentOk: true });
  }
  private planWalk(tile: TileXY): TileXY[] | null {
    const me = this.me, cur = this.scenes.current;
    if (!me || !cur) return null;
    const from = { x: Math.floor(me.walker.pos.x), y: Math.floor(me.walker.pos.z) };
    const nav = useGame.getState().space === 'out' ? this.nav() : null;
    if (nav && this.map) return findPath(nav, this.map, from, tile, { adjacentOk: true });
    return cur.walkable({ x: tile.x + 0.5, y: tile.y + 0.5 }) ? [from, tile] : null;   // interiors: a straight walk
  }
  applyQuality(q: ClientSettings['quality']): void {
    const level: QualityLevel = resolveQuality(q, deviceInfo());
    this.renderer?.setQuality(level);
  }

  // ---------- called by SpaceManager and the net handlers
  /** After a scene switch: the store's space, you at `spawn`, everyone else from the occupants, then every module's onSpace. */
  enteredSpace(snap: SpaceSnapshot, spawn: MoveState): void {
    useGame.setState({ space: snap.space });
    this.placeMe(spawn);
    this.spawnOccupants(snap.occupants);
    this.sound.setIndoor(snap.kind !== 'out');
    this.socket?.resetMoves();
    this.tapToWalk.cancel();
    for (const m of handlerModules()) this.safely(() => m.handlers.onSpace?.(snap, this));
  }
  placeMe(spawn: MoveState): void {
    this.lastSpawn = spawn;
    const me = this.me, cur = this.scenes.current;
    if (!me || !cur) return;
    if (me.avatar.root.parent !== cur.scene) cur.scene.add(me.avatar.root);
    me.walker.place(spawn.x, spawn.y, spawn.f);
    this.camera?.frame(this.indoorFraming());
    this.camera?.snap(me.walker.pos);
  }
  /** Replaces the other residents with this space's occupants (a switch or a resync). */
  spawnOccupants(list: readonly Occupant[]): void {
    const keep = new Set(list.map((o) => o.r));
    for (const r of [...this.remotes.keys(), ...this.spawning.keys()]) if (!keep.has(r)) this.despawn(r);
    for (const o of list) void this.spawn(o.r, o.s);
  }
  async spawn(r: ResidentId, s: MoveState): Promise<void> {
    const cur = this.scenes.current;
    if (!cur || r === useGame.getState().you?.resident) return;
    const existing = this.remotes.get(r);
    if (existing) {
      if (existing.avatar.root.parent !== cur.scene) cur.scene.add(existing.avatar.root);
      existing.snap(s, (p) => cur.heightAt(p));
      return;
    }
    const res = useGame.getState().residents[r];
    if (!res) return;
    const token = ++this.spawnSeq;
    this.spawning.set(r, token);
    let avatar: Avatar;
    try { avatar = await this.makeAvatar(res); }
    catch (e) { if (this.spawning.get(r) === token) this.spawning.delete(r); if (IS_DEV) console.warn('spawn failed', r, e); return; }
    const now = this.scenes.current;
    if (this.spawning.get(r) !== token || !now) { avatar.dispose(); return; }
    this.spawning.delete(r);
    const rr = new RemoteResident(r, avatar);
    this.remotes.set(r, rr);
    this.looks.set(r, lookKey(res));
    now.scene.add(avatar.root);
    rr.snap(s, (p) => now.heightAt(p));
    this.refreshTag(r);
  }
  despawn(r: ResidentId): void {
    this.spawning.delete(r);
    const rr = this.remotes.get(r);
    if (!rr) return;
    rr.avatar.dispose();
    this.remotes.delete(r);
    this.held.delete(r);
    this.looks.delete(r);
  }
  /** A resident's tag from the store: name, friend colour, and away (presence grace); gone when they are not in town. */
  refreshTag(r: ResidentId): void {
    const s = useGame.getState(), res = s.residents[r], p = s.presence[r];
    if (!res || !p) { this.tags?.remove(r); return; }
    this.tags?.set(r, res.name, res.color, p.away);
  }
  /** A resident's name, look or colour changed: rebuild their doll in place and refresh their tag. */
  restyle(r: ResidentId): void {
    this.refreshTag(r);
    if (this.me?.r === r) { void this.ensureMe(); return; }
    const rr = this.remotes.get(r), cur = this.scenes.current, res = useGame.getState().residents[r];
    if (!rr || !cur || !res || this.looks.get(r) === lookKey(res)) return;   // house, door or role changes keep the doll
    const s: MoveState = { x: rr.pos.x, y: rr.pos.z, f: rr.avatar.root.rotation.y, a: 'idle', tool: rr.lastTool };
    this.despawn(r);
    void this.spawn(r, s);
  }
  afterPatch(patch: PrivatePatch): void {
    const you = useGame.getState().you;
    if (you) this.scenes.out?.refreshPerResident(you);
    for (const m of handlerModules()) this.safely(() => m.handlers.onPatch?.(patch, this));
  }
  /** Friends' names over their houses while you are outdoors (revision 9 item 3); none indoors. */
  syncHouseTags(): void {
    const s = useGame.getState();
    this.tags?.syncHouses('residents', s.space === 'out' ? residentHouseTags(this.objects('house'), s.residents) : [], (p) => this.scenes.out?.heightAt(p) ?? 0);
  }

  // ---------- socket callbacks
  private async onSnapshot(s: Snapshot, socket: TownSocket): Promise<void> {
    const run = ++this.snapRun;
    const end = this.holdSpaceEvents(true);
    try {
      useGame.getState().applySnapshot(s);                       // also sets `map` in the store
      this.map = useGame.getState().map;
      this.navDirty = true;
      this.ticker.reset();
      this.ticker.tick(socket.clock.now(), s.town.timeZone);     // the HUD clock at once; a first tick never emits
      this.lastClockTick = this.now();
      this.sound.setMood(moodAt(useGame.getState().clock.minuteOfDay));
      await this.ensureMe();
      if (run !== this.snapRun) return;
      this.tags?.self(s.you.resident);
      const present = new Set(s.presence.map((p) => p.r));
      for (const r of s.residents) if (present.has(r.id)) this.refreshTag(r.id); else this.tags?.remove(r.id);
      await this.switchSpace(s.space, s.spawn);
      if (run !== this.snapRun) return;
      this.syncHouseTags();
      for (const m of handlerModules()) this.safely(() => m.handlers.onSnapshot?.(s, this));
      if (useGame.getState().phase === 'connecting') useGame.getState().setPhase('inTown');
    } catch (e) {
      if (IS_DEV) console.error('snapshot failed', e);
      useGame.getState().toast('errors.server_error', undefined, 'warn');
      // The first snapshot never got you in: back to the town list rather than the boot "Loading…" for good.
      if (run === this.snapRun && this.sock === socket && useGame.getState().phase === 'connecting') this.cmd.leaveTown();
    } finally { end(); }
  }
  private onMoves(m: Extract<S2C, { t: 'moves' }>): void {
    for (const { r, ...s } of m.list) this.remotes.get(r)?.buffer.push(m.at, s);   // unknown residents wait for their space.enter
  }
  /** The server put you back. A small correction mid tap-to-walk re-plans from where you now stand (the walk goes on); a big one,
   *  or one that keeps happening, ends it. */
  private onCorrect(c: Extract<S2C, { t: 'correct' }>): void {
    const me = this.me;
    const off = me ? Math.hypot(me.walker.pos.x - c.s.x, me.walker.pos.z - c.s.y) : Infinity;
    me?.walker.place(c.s.x, c.s.y, c.s.f);
    if (!this.tapToWalk.active) return;
    const goal = this.walkGoal;
    if (goal && off <= tuning.net.snapTiles && this.walkReplans++ < MAX_REPLANS) {
      const path = this.planWalk(goal);
      if (path) { this.tapToWalk.reroute(path); return; }
    }
    this.tapToWalk.cancel();
  }
  private onEvents(list: readonly TownEvent[]): void {
    const others = handlerModules().filter((m) => m.handlers !== coreHandlers);
    for (const e of list) {
      if (this.spaceEvents && HELD_WHILE_SWITCHING.has(e.k)) { this.spaceEvents.push(e); continue; }   // see holdSpaceEvents
      if (e.k === 'object.added' || e.k === 'object.updated') { this.outObjects.set(e.obj.id, e.obj); this.navDirty = true; }
      else if (e.k === 'object.removed') { this.outObjects.delete(e.id); this.navDirty = true; }
      for (const h of [coreHandlers, ...others.map((m) => m.handlers)]) {
        const fn = h.events?.[e.k] as ((e: TownEvent, g: Game) => void) | undefined;
        if (fn) this.safely(() => fn(e, this));
      }
    }
  }
  private onState(st: SocketState, socket: TownSocket): void {
    const reconnecting = !(st === 'live' || st === 'closed' || st === 'idle' || st === 'updateRequired');
    useGame.setState({ socket: st, banner: reconnecting ? 'reconnecting' : null });
    if (st === 'live') for (const w of this.liveWaiters.splice(0)) w();
    if (st === 'updateRequired') { useGame.getState().setPhase('updateRequired'); return; }   // the update phase screen (revision 9 item 1)
    if (st !== 'closed') return;
    // Invariant: every deliberate close (connect() replacing the socket, teardown) nulls `this.sock` first, so this callback's
    // `this.socket === socket` guard drops it and a 'closed' that reaches here always comes from the server.
    // The server stopped us (another device, removed from the town, closed town, ticket refused): back to the town list.
    const s = useGame.getState();
    const notice = stopNotice(socket.lastClose, socket.lastHttp, s.me?.towns.find((x) => x.id === socket.town)?.name ?? null);
    s.toast(notice.key, notice.params, 'warn');
    if (notice.key === 'hud.removedFromTown') {
      // Removed from the town (M2 playtest): it leaves your list now (the towns screen re-reads the rest from the server), and the towns
      // screen keeps saying why, for whoever was not looking during the toast (v1 round 3: a desktop tab in the background).
      if (s.me) s.setMe({ ...s.me, towns: s.me.towns.filter((x) => x.id !== socket.town) });
      s.setTownsNotice(notice);
    }
    this.cmd.leaveTown();
  }

  // ---------- input
  private onTap(ndc: { x: number; y: number }): void {
    const cur = this.scenes.current, cam = this.camera;
    if (!cur || !cam || !this.me || this.switching) return;
    const g = pickGround(ndc, cam.camera, (p) => cur.heightAt(p), cur.bounds);
    if (!g) return;
    const hit: PickHit = { ...g };
    if (useGame.getState().space === 'out') { const o = this.scenes.out?.picking.objectAt(g.tile); if (o) hit.object = o; }
    const near = [...this.remotes.values()].find((rr) => Math.hypot(rr.pos.x - g.point.x, rr.pos.z - g.point.y) < TAP_RESIDENT);
    if (near) hit.resident = near.r;
    const c = this.interactions.fromTap(hit);
    if (c) { this.walkTo({ x: Math.floor(c.at.x), y: Math.floor(c.at.y) }, () => { this.interactions.runCandidate(c); }); return; }
    this.walkTo(g.tile);
  }

  // ---------- the eight core systems (§8.3), plus registry ExtSystems right after their slot
  private systems(): System[] {
    const core: System[] = [
      { name: 'input', update: (f) => this.inputStep(f) },
      { name: 'movement', update: (f) => this.moveStep(f) },
      { name: 'netInterp', update: (f) => this.interpStep(f) },
      { name: 'animation', update: (f) => this.animStep(f) },
      this.interactions,
      { name: 'villagers', update: () => {} },                     // M2-T11 adds an ExtSystem here
      { name: 'camera', update: (f) => this.cameraStep(f) },
      { name: 'render', update: (f) => this.renderStep(f) },
    ];
    const ext = (systemModules as readonly { system: ExtSystem }[]).map((m) => m.system);
    const out: System[] = [];
    for (const s of core) { out.push(s); for (const e of ext) if (e.slot === s.name) out.push(e.create(this)); }
    return out;
  }
  private inputStep(f: Frame): void {
    const me = this.me, input = this.input;
    if (!me || !input) { this.intent = { dx: 0, dz: 0, running: false }; return; }
    const v = input.vector();
    if (Math.hypot(v.x, v.y) > 0.05 && this.tapToWalk.active) this.tapToWalk.cancel();   // the stick or keys take over
    if (this.switching || !this.scenes.current) return;           // moveStep holds still: the walk's stuck clock must not run either
    if (this.tapToWalk.active) {
      const d = this.tapToWalk.steer({ x: me.walker.pos.x, y: me.walker.pos.z }, f.dt * 1000);   // simulation time, as the walker moves
      this.intent = d ? { dx: d.dx, dz: d.dy, running: v.running } : { dx: 0, dz: 0, running: false };
    } else this.intent = { dx: v.x, dz: v.y, running: v.running };   // camera yaw is fixed at 0: screen up = −Z = north (D-07)
  }
  private moveStep(f: Frame): void {
    const me = this.me;
    if (!me || !this.scenes.current || this.switching) return;
    me.walker.move(f.dt, this.intent.dx, this.intent.dz, this.intent.running);
    this.socket?.sendMove(me.walker.state(useGame.getState().tool));
  }
  private interpStep(f: Frame): void {
    const cur = this.scenes.current;
    if (!cur) return;
    const renderAt = f.now - tuning.net.interpDelayMs, h = (p: Vec2) => cur.heightAt(p);
    for (const rr of this.remotes.values()) { rr.update(renderAt, f.dt, h); this.syncHeld(rr); }
  }
  /** Puts a remote resident's relayed tool in their hand (a stale load is dropped when the tool changed again meanwhile). */
  private syncHeld(rr: RemoteResident): void {
    const want = rr.lastTool;
    if ((this.held.get(rr.r) ?? null) === want) return;
    this.held.set(rr.r, want);
    if (!want) { rr.avatar.hold(null); return; }
    void this.assets.model(want).then((m) => { if (this.held.get(rr.r) === want && this.remotes.get(rr.r) === rr) rr.avatar.hold(m, 'right'); });
  }
  private animStep(f: Frame): void {
    this.me?.avatar.update(f.dt);
    for (const rr of this.remotes.values()) rr.avatar.update(f.dt);
  }
  private framed: { key: string; f: Framing } | null = null;
  private decorUi: DecorateUi | null = null;
  /** Decorate mode in a room (ui/screens/decorate.tsx) frames it above its tray, clear of its HUD row; null goes back to the room's
   *  own framing. */
  frameForDecorate(ui: DecorateUi | null): void { this.decorUi = ui; }
  /** Indoors the camera holds the whole room in view below the HUD row, or follows you through a big one; outdoors (null) it follows you (M1 playtest). */
  private indoorFraming(): Framing | null {
    const cur = this.scenes.current;
    if (!cur || cur.scene === this.scenes.out?.scene) return null;
    const W = innerWidth, H = innerHeight, b = cur.bounds, d = this.decorUi;
    const key = `${b.x},${b.y},${b.w},${b.h},${W},${H}${d ? `|${JSON.stringify(d)}` : ''}`;
    if (this.framed?.key !== key) {
      this.framed = { key, f: d ? decorateFraming(b, WALL_HEIGHT, W, H, d) : roomFraming(b, WALL_HEIGHT, W, H) };
    }
    return this.framed.f;
  }
  private cameraStep(f: Frame): void {
    const me = this.me, cam = this.camera;
    if (!me || !cam || !this.scenes.current) return;
    cam.frame(this.indoorFraming());
    cam.follow(me.walker.pos, f.dt);
    worldUniforms.uTime.value += f.dt;
    // Dolls keep their own (unpatched, shared) toy materials, so the curved world bends them here, on the CPU.
    this.bend(me.avatar.root, me.walker.pos);
    for (const rr of this.remotes.values()) this.bend(rr.avatar.root, rr.pos);
    cam.camera.updateMatrixWorld();                               // follow() moved it: aim at this frame's view, not the last one
    // The occlusion fade (engine/shader/fade.ts) follows you and the nearest others (friends among the pines, or only their floating name
    // tag would show): whatever stands clearly between the camera and one of them fades whole. Feet unbent, as the world is measured.
    setFadeCamera(cam.camera.position);
    const me0 = me.walker.pos;
    const near = [...this.remotes.values()].filter((rr) => rr.avatar.root.visible)
      .sort((a, b) => a.pos.distanceToSquared(me0) - b.pos.distanceToSquared(me0)).slice(0, FADE_VIEWERS - 1);
    setViewers([me0, ...near.map((rr) => rr.pos)]);
  }
  private bend(root: THREE.Object3D, pos: THREE.Vector3): void {
    const c = worldUniforms.uCamXZ.value, dx = pos.x - c.x, dz = pos.z - c.y, d2 = dx * dx + dz * dz;
    root.position.y = pos.y - d2 * worldUniforms.uCurve.value;
    root.visible = d2 <= tuning.camera.horizon * tuning.camera.horizon;
  }
  private renderStep(f: Frame): void {
    const cur = this.scenes.current, r = this.renderer, cam = this.camera;
    if (!cur || !r || !cam) return;
    const s = useGame.getState();
    // Once a second of town time, or at once when the town clock went backwards (a dev-clock change, or a resync that lowered the
    // offset): waiting for town time to catch up would freeze the HUD clock, the light and the music for hours.
    if (s.town && (f.now - this.lastClockTick >= 1000 || f.now < this.lastClockTick)) {
      this.lastClockTick = f.now;
      this.ticker.tick(f.now, s.town.timeZone);
      this.sound.setMood(moodAt(useGame.getState().clock.minuteOfDay));
    }
    cur.tick?.(f.dt, useGame.getState().clock.minuteOfDay);
    r.render(cur.scene, cam.camera);
    this.heads.clear();
    if (this.me) this.heads.set(this.me.r, this.me.walker.pos);
    for (const rr of this.remotes.values()) this.heads.set(rr.r, rr.pos);
    this.tags?.update(cam.camera, this.heads);
    if (s.settings.perfOverlay) {                             // only when asked for: the Settings toggle, or ?perf (engine/perf.ts)
      this.perf ??= new PerfOverlay(document.body);
      if (f.frameNo % PERF_EVERY === 0) this.perf.update(r.stats());
    } else if (this.perf) { this.perf.dispose(); this.perf = null; }
    // ready() waits until the world is really on screen: every shader of the scene compiled (a slow GPU, SwiftShader in e2e, finishes
    // compiling long after the first frame was issued) and then two more frames drawn with them.
    if (!this.rendered && s.phase === 'inTown') {
      if (this.settle === null) {
        this.settle = { frames: -1 };
        const settle = this.settle, compile = (r.renderer as { compileAsync?: (s: THREE.Object3D, c: THREE.Camera) => Promise<unknown> }).compileAsync;
        void Promise.resolve(compile ? compile.call(r.renderer, cur.scene, cam.camera) : undefined).catch(() => {})
          .then(() => { settle.frames = 2; });
      } else if (this.settle.frames > 0 && --this.settle.frames === 0) {
        this.rendered = true;
        this.settle = null;
        for (const w of this.frameWaiters.splice(0)) w();
      }
    }
  }

  // ---------- helpers
  /** Your doll and walker, built (or rebuilt after a look change) from your resident record. */
  private async ensureMe(): Promise<void> {
    const s = useGame.getState(), you = s.you, res = you ? s.residents[you.resident] : undefined;
    if (!you || !res) return;
    const key = lookKey(res);
    if (this.me && this.me.r === res.id && this.me.key === key) return;
    const build = ++this.meBuild;
    const avatar = await this.makeAvatar(res);
    if (build !== this.meBuild) { avatar.dispose(); return; }
    const old = this.me, walker = new Walker(avatar, this.here, () => this.blockers());
    if (old) {
      walker.place(old.walker.pos.x, old.walker.pos.z, old.walker.yaw);
      old.avatar.root.parent?.add(avatar.root);
      old.avatar.dispose();
    }
    this.me = { r: res.id, avatar, walker, key };
    // No previous doll (a newer build overtook the snapshot's, which then placed nobody): put this one where you were placed.
    if (!old && this.lastSpawn) this.placeMe(this.lastSpawn);
  }
  /** A resident's doll, with its silhouette for when something that never fades hides them (engine/silhouette.ts). */
  private async makeAvatar(res: ResidentPublic): Promise<Avatar> {
    const avatar = Avatar.create(await this.assets.rig(), lookToDoll(res.look, res.color), tuning.move.residentHeight);
    addSilhouette(avatar.root);
    return avatar;
  }
  private blockers(): { x: number; z: number; r: number }[] {
    return [...this.remotes.values()].map((rr) => ({ x: rr.pos.x, z: rr.pos.z, r: tuning.move.radius }));
  }
  private uiRoot(): HTMLElement { return document.getElementById('ui') ?? document.body; }
  /** One failing feature handler never stops the others (or the socket). */
  private safely(fn: () => void): void {
    try { fn(); } catch (e) { if (IS_DEV) console.error('net handler failed', e); }
  }
}
